/**
 * MobileNav - Bottom navigation bar for mobile devices
 * Shows on screens < 768px (md breakpoint)
 */

import { NavLink } from 'react-router-dom';
import { Shield, BriefcaseBusiness, Radar, Clock3, Gauge } from 'lucide-react';
import { useAuth } from '@lib/auth';
import { featureFlags } from '@lib/featureFlags';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
}

export function MobileNav(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navItems: NavItem[] = featureFlags.salesOsV2Enabled
    ? [
        { to: '/scorecard', label: 'Score', icon: <Gauge size={22} /> },
        { to: '/sales/opportunities', label: 'Opps', icon: <BriefcaseBusiness size={22} /> },
        { to: '/sales/activity', label: 'Activity', icon: <Radar size={22} /> },
        { to: '/log', label: 'Log', icon: <Clock3 size={22} /> },
      ]
    : [{ to: '/scorecard', label: 'Score', icon: <Gauge size={22} /> }];

  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-ybh-bg-secondary border-t border-white/[0.04] safe-area-bottom"
      aria-label="Mobile navigation"
      role="navigation"
    >
      <div className="flex items-center justify-around h-16 px-2" role="list">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            aria-label={item.label}
            className={({ isActive }) => `
              flex flex-col items-center justify-center py-2 px-3 rounded-lg
              transition-colors min-w-[64px]
              ${isActive ? 'text-ybh-orange' : 'text-ybh-text-muted'}
            `}
          >
            {item.icon}
            <span className="text-[10px] mt-1 font-medium" aria-hidden="true">{item.label}</span>
          </NavLink>
        ))}

        {/* Admin icon - only for admins */}
        {isAdmin && (
          <NavLink
            to="/admin"
            aria-label="Admin Panel"
            className={({ isActive }) => `
              flex flex-col items-center justify-center py-2 px-3 rounded-lg
              transition-colors min-w-[64px]
              ${isActive ? 'text-ybh-warning' : 'text-ybh-text-muted'}
            `}
          >
            <Shield size={22} />
            <span className="text-[10px] mt-1 font-medium" aria-hidden="true">Admin</span>
          </NavLink>
        )}
      </div>
    </nav>
  );
}
